import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";

import { zkApi } from "./zkApi";
import DoorAccessDialog from "./DoorAccessDialog";
import ZkRefusedScans from "./ZkRefusedScans";
import {
  ACCESS_EVENT_LABELS, DECISION_META, MEMBER_STATUS_META, accessStateMeta, apiError, chipSx, metaFor, reasonLabel,
} from "./zkStatusStyles";
import { backButtonSx, cancelButtonSx } from "../hpcAdminStatusStyles";

const rowActionSx = { ...cancelButtonSx, minWidth: 0, px: 1, py: 0.25 };
const UNBLOCK_ALL_CONFIRM = "UNBLOCK ALL";

const isPending = (r) => ["pending", "sent"].includes(r.cmd_status) && r.cmd_action;

const FILTERS = [
  { value: "would_block", label: "To block", test: (r) => r.decision === "block" && r.applied !== "blocked" },
  { value: "blocked", label: "Blocked", test: (r) => r.applied === "blocked" },
  { value: "pending", label: "In progress", test: (r) => isPending(r) || r.attempts > 0 },
  { value: "flagged", label: "Check", test: (r) => r.flagged },
  { value: "allow", label: "Allowed", test: (r) => r.decision === "allow" },
  { value: "manual", label: "Staff / admins", test: (r) => r.manual },
  { value: "skip", label: "Not managed", test: (r) => r.decision === "skip" && !r.manual },
  { value: "all", label: "All", test: () => true },
];

function ModeBanner({ settings, device }) {
  if (!settings) return null;
  const rules = `Pending blocked: ${settings.block_pending ? "yes" : "no"} · Admin statuses blocked: `
    + `${settings.block_admin_statuses ? "yes" : "no"} · Frozen: ${settings.block_frozen ? "blocked" : "allowed"}`
    + ` · Grace days: ${settings.grace_days} · Max ${settings.max_cmds_per_poll} commands per sync`
    + ` · Last sync: ${device?.last_sync || "never"}`
    + ` · Protected PINs: ${settings.protected_pins?.length ? settings.protected_pins.join(", ") : "none"} (+ all super admins)`;
  let severity = "info";
  let text = "Door access control is OFF. This is a preview only: nothing is recorded and nothing is sent to the device.";
  if (settings.mode === "dryrun") {
    severity = "warning";
    text = "DRY-RUN: TEBA records what it would do and logs it. Nothing is sent to the device.";
  } else if (settings.mode === "on" && device?.paused) {
    severity = "error";
    text = "ON but PAUSED: nothing is sent to the device. Press Resume to continue.";
  } else if (settings.mode === "on") {
    severity = "success";
    text = "ON: TEBA blocks and unblocks HPU members at the door (synced about every minute).";
  }
  return (
    <Alert severity={severity} sx={{ mb: 2 }}>
      <b>{settings.mode.toUpperCase()}{device?.paused ? " · PAUSED" : ""}</b> - {text}
      <Typography variant="caption" sx={{ display: "block", opacity: 0.8 }}>{rules}</Typography>
    </Alert>
  );
}

function PilotBanner({ settings }) {
  const pins = settings?.only_pins;
  if (!pins) return null;          // no pilot: every eligible PIN is managed
  const invalid = settings.only_pins_invalid || [];
  return (
    <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
      <b>PILOT: {pins.length ? `only PIN${pins.length > 1 ? "s" : ""} ${pins.join(", ")}` : "no PINs"}</b>
      {" "}- every other PIN is not managed (Outside pilot). PINs TEBA blocked earlier are still unblocked.
      {invalid.length > 0 && (
        <Typography variant="caption" sx={{ display: "block", color: "#ff9800" }}>
          Ignored invalid entries in ZK_ACCESS_ONLY_PINS: {invalid.join(", ")}
        </Typography>
      )}
    </Alert>
  );
}

function GuardBanner({ guard, busy, onApprove }) {
  if (!guard?.tripped) return null;
  return (
    <Alert severity="error" sx={{ mb: 2 }} action={(
      <Button color="inherit" size="small" disabled={busy} onClick={() => onApprove(guard.blocks_needed)}>
        Approve {guard.blocks_needed} blocks
      </Button>
    )}>
      <b>Mass-change guard:</b> {guard.blocks_needed} members would be blocked at once
      ({guard.managed} managed PINs; limit {guard.max} or {guard.pct}%). No blocks are sent until you approve.
      Unblocks still go out. Check the "To block" list first.
    </Alert>
  );
}

function UnblockAllDialog({ open, onClose, onConfirm, busy }) {
  const [text, setText] = useState("");
  useEffect(() => { if (open) setText(""); }, [open]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Unblock everyone</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Emergency: pauses door access control, cancels blocks that are not sent yet, and sends an unblock
          for every member TEBA has blocked. Works in every mode. Resume afterwards to start blocking again.
        </Typography>
        <TextField fullWidth size="small" label={`Type ${UNBLOCK_ALL_CONFIRM} to confirm`} value={text}
          onChange={(e) => setText(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="error" disabled={busy || text !== UNBLOCK_ALL_CONFIRM}
          onClick={() => onConfirm(text)}>
          Unblock all
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Advanced door access (technical detail). Shown collapsed under the simple "Gym door" view.
// refreshKey: bumped by the simple view after a change; onChanged: tells the simple view to reload.
// initialFilter: optional FILTERS value from a deep link (e.g. ?filter=blocked); unknown values are ignored.
export default function ZkDoorAccess({ devices, refreshKey = 0, onChanged, initialFilter }) {
  const [sn, setSn] = useState("");
  const [data, setData] = useState(null);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState(
    () => (FILTERS.some((f) => f.value === initialFilter) ? initialFilter : "would_block"),
  );
  const [busy, setBusy] = useState(false);
  const [overrideTarget, setOverrideTarget] = useState(null);
  const [unblockAllOpen, setUnblockAllOpen] = useState(false);

  useEffect(() => {
    if (!sn && devices.length) setSn(devices[0].serial_number);
  }, [devices, sn]);

  const load = useCallback(async () => {
    if (!sn) return;
    setLoading(true);
    try {
      const [overview, logRes] = await Promise.all([
        zkApi("get", "/hpc/admin/zk/access", { params: { serial_number: sn } }),
        zkApi("get", "/hpc/admin/zk/access/log", { params: { serial_number: sn, limit: 100 } }),
      ]);
      setData(overview.data);
      setLog(logRes.data || []);
      setError("");
    } catch (e) {
      setError(apiError(e, "Could not load door access"));
    } finally {
      setLoading(false);
    }
  }, [sn]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // One admin action: POST, toast the message, reload.
  const act = async (path, body, fallback) => {
    setBusy(true);
    try {
      const res = await zkApi(path.startsWith("DELETE ") ? "delete" : "post",
        path.replace(/^DELETE /, ""), body ? { data: { serial_number: sn, ...body } } : {});
      toast.success(res.data?.message || "Done");
      load();
      if (onChanged) onChanged();
      return true;
    } catch (e) {
      toast.error(apiError(e, fallback));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const evaluate = () => act("/hpc/admin/zk/access/evaluate", {}, "Could not record the evaluation");
  const syncNow = () => act("/hpc/admin/zk/access/sync-now", {}, "Could not run the sync");
  const setPaused = (paused) => act("/hpc/admin/zk/access/pause", { paused }, "Could not change pause");
  const approveGuard = (count) => act("/hpc/admin/zk/access/approve-guard", { count }, "Could not approve");
  const unblockAll = async (confirm) => {
    if (await act("/hpc/admin/zk/access/unblock-all", { confirm }, "Unblock all failed")) setUnblockAllOpen(false);
  };
  const removeOverride = (row) => act(`DELETE /hpc/admin/zk/access/overrides/${row.override.id}`, null,
    "Could not remove the override");

  const items = useMemo(() => data?.items || [], [data]);
  const counts = useMemo(
    () => Object.fromEntries(FILTERS.map((f) => [f.value, items.filter(f.test).length])),
    [items],
  );
  const visible = useMemo(() => items.filter((FILTERS.find((f) => f.value === filter) || FILTERS[0]).test),
    [items, filter]);

  const mode = data?.settings?.mode;
  const paused = !!data?.device?.paused;

  const columns = [
    { field: "pin", headerName: "PIN", width: 80 },
    { field: "device_name", headerName: "Name on device", width: 130, valueGetter: (v) => v || "-" },
    {
      field: "full_name", headerName: "Member", flex: 1, minWidth: 190,
      renderCell: ({ row }) => (row.membership_id
        ? <span>{row.full_name} <span style={{ opacity: 0.7 }}>({row.membership_id})</span></span>
        : <span style={{ opacity: 0.6 }}>-</span>),
    },
    {
      field: "member_status", headerName: "Membership", width: 120,
      renderCell: ({ value }) => (value ? (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Chip size="small" label={metaFor(MEMBER_STATUS_META, value).label} sx={chipSx(metaFor(MEMBER_STATUS_META, value).color)} />
        </Box>
      ) : "-"),
    },
    { field: "effective_expiry_date", headerName: "Expiry", width: 100, valueGetter: (v) => v || "-" },
    {
      field: "decision", headerName: "Should be", width: 115,
      renderCell: ({ value }) => {
        const m = value === "block" ? { label: "Blocked", color: DECISION_META.block.color } : metaFor(DECISION_META, value);
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Chip size="small" label={m.label} sx={chipSx(m.color)} />
          </Box>
        );
      },
    },
    {
      field: "reason", headerName: "Why", flex: 1, minWidth: 190,
      renderCell: ({ row }) => (
        <span style={{ color: row.flagged ? "#ff9800" : undefined }}>
          {reasonLabel(row.reason)}
          {row.override ? ` (until ${row.override.valid_until}${row.override.note ? ` - ${row.override.note}` : ""})` : ""}
        </span>
      ),
    },
    {
      field: "applied", headerName: "On device", width: 150,
      renderCell: ({ row }) => {
        const m = accessStateMeta(row);
        return (
          <Tooltip title={row.applied_at ? `Confirmed ${row.applied_at}` : "Device default until TEBA sends a command"}>
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <Chip size="small" label={m.label} sx={chipSx(m.color)} />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "last_result", headerName: "Last result", width: 140,
      renderCell: ({ row }) => (
        <Tooltip title={row.next_attempt_at ? `Next try ${row.next_attempt_at}` : ""}>
          <span style={{ opacity: 0.8 }}>{row.last_result || "-"}</span>
        </Tooltip>
      ),
    },
    {
      field: "actions", headerName: "", width: 200, sortable: false, filterable: false,
      renderCell: ({ row }) => (row.protected ? (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Chip size="small" label={row.reason === "super_admin" ? "Super admin - protected" : "Protected"}
            sx={chipSx("#7e57c2")} />
        </Box>
      ) : row.manual || (row.kind === "member" && row.membership_id) ? (
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", height: "100%" }}>
          <Button size="small" variant="outlined" sx={rowActionSx} disabled={busy}
            onClick={() => setOverrideTarget({ ...row, action: "block" })}>Block</Button>
          <Button size="small" variant="outlined" sx={rowActionSx} disabled={busy}
            onClick={() => setOverrideTarget({ ...row, action: "allow" })}>Unblock</Button>
          {row.override && (
            <Button size="small" variant="outlined" sx={rowActionSx} disabled={busy} onClick={() => removeOverride(row)}>
              Remove
            </Button>
          )}
        </Box>
      ) : null),
    },
  ];

  const logColumns = [
    { field: "created_at", headerName: "Time", width: 160 },
    { field: "event", headerName: "Event", width: 160, valueGetter: (v) => ACCESS_EVENT_LABELS[v] || v },
    { field: "pin", headerName: "PIN", width: 80, valueGetter: (v) => v || "-" },
    {
      field: "full_name", headerName: "Member", flex: 1, minWidth: 180,
      valueGetter: (v, row) => (row.membership_id ? `${v} (${row.membership_id})` : "-"),
    },
    { field: "reason", headerName: "Why", flex: 1, minWidth: 150, valueGetter: (v) => reasonLabel(v) },
    {
      field: "detail", headerName: "Detail", flex: 1, minWidth: 170,
      valueGetter: (v, row) => [v, row.cmd_id ? `cmd ${row.cmd_id}` : "", row.return_code != null ? `Return=${row.return_code}` : ""]
        .filter(Boolean).join(" · "),
    },
    { field: "actor", headerName: "By", width: 110, valueGetter: (v) => v || "-" },
  ];

  const s = data?.summary;
  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <ModeBanner settings={data?.settings} device={data?.device} />
      <PilotBanner settings={data?.settings} />
      <GuardBanner guard={data?.guard} busy={busy} onApprove={approveGuard} />

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
        {devices.length > 1 && (
          <TextField select size="small" label="Device" value={sn} onChange={(e) => setSn(e.target.value)} sx={{ minWidth: 220 }}>
            {devices.map((d) => <MenuItem key={d.serial_number} value={d.serial_number}>{d.name || d.serial_number}</MenuItem>)}
          </TextField>
        )}
        {s && (
          <Typography variant="body2">
            To block <b style={{ color: s.would_block ? "#f44336" : undefined }}>{s.would_block}</b> ·
            to unblock <b>{s.would_unblock}</b> · blocked <b>{s.blocked}</b> · in progress <b>{s.in_flight}</b> ·
            failing <b style={{ color: s.failed ? "#ff9800" : undefined }}>{s.failed}</b> ·
            check <b style={{ color: s.flagged ? "#ff9800" : undefined }}>{s.flagged}</b> ·
            overrides <b>{s.overrides}</b> <span style={{ opacity: 0.7 }}>(as of {data.today})</span>
          </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {mode === "dryrun" && (
          <Tooltip title="Store the decisions below and log new would-block / would-unblock entries. Nothing is sent.">
            <span>
              <Button size="small" variant="outlined" sx={backButtonSx} startIcon={<FactCheckOutlinedIcon />}
                disabled={busy || !sn} onClick={evaluate}>
                Record dry-run
              </Button>
            </span>
          </Tooltip>
        )}
        <Tooltip title={mode === "on" ? "Run the sync now instead of waiting for the next heartbeat" : "Records the evaluation (sends only when ON)"}>
          <span>
            <Button size="small" variant="outlined" sx={backButtonSx} startIcon={<SyncOutlinedIcon />}
              disabled={busy || !sn || !mode || mode === "off" || paused} onClick={syncNow}>
              Sync now
            </Button>
          </span>
        </Tooltip>
        <Button size="small" variant="outlined" sx={backButtonSx} disabled={busy || !sn || !data}
          startIcon={paused ? <PlayCircleOutlineIcon /> : <PauseCircleOutlineIcon />} onClick={() => setPaused(!paused)}>
          {paused ? "Resume" : "Pause"}
        </Button>
        <Button size="small" variant="outlined" sx={backButtonSx} startIcon={<RefreshOutlinedIcon />} disabled={loading} onClick={load}>
          Reload
        </Button>
      </Box>

      <ToggleButtonGroup size="small" exclusive value={filter} onChange={(e, v) => v && setFilter(v)} sx={{ mb: 1, flexWrap: "wrap" }}>
        {FILTERS.map((f) => <ToggleButton key={f.value} value={f.value}>{f.label} ({counts[f.value] || 0})</ToggleButton>)}
      </ToggleButtonGroup>
      <Box sx={{ height: "55vh" }}>
        <DataGrid rows={visible} columns={columns} loading={loading} getRowId={(r) => r.pin} disableRowSelectionOnClick
          localeText={{ noRowsLabel: filter === "would_block" ? "Nobody to block" : "No PINs" }} />
      </Box>
      <Typography variant="caption" sx={{ opacity: 0.7 }}>
        Staff PINs and device admins are only blocked manually. Super admins and protected PINs are never
        blocked; ignored, unlinked and needs-review PINs are not managed. Block / Unblock create an override
        until a date, so the regular sync keeps it.
      </Typography>

      <Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>Access log</Typography>
      <Box sx={{ height: 320 }}>
        <DataGrid rows={log} columns={logColumns} loading={loading} getRowId={(r) => r.id} disableRowSelectionOnClick
          density="compact" localeText={{ noRowsLabel: "No entries yet" }} />
      </Box>

      <Typography variant="h6" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>Refused at the door (history)</Typography>
      <ZkRefusedScans />

      <Box sx={{ mt: 4, p: 2, border: "1px solid #f44336", borderRadius: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#f44336" }}>Danger zone</Typography>
        <Typography variant="body2" sx={{ mb: 1.5, opacity: 0.85 }}>
          Emergency only: lets everyone TEBA has blocked open the door again and pauses door control.
        </Typography>
        <Button variant="contained" color="error" startIcon={<LockOpenOutlinedIcon />} disabled={busy || !sn}
          onClick={() => setUnblockAllOpen(true)}>
          Open door for everyone (Unblock all)
        </Button>
      </Box>

      <DoorAccessDialog target={overrideTarget} sn={sn} today={data?.today} onClose={() => setOverrideTarget(null)}
        onDone={() => { setOverrideTarget(null); load(); if (onChanged) onChanged(); }} />
      <UnblockAllDialog open={unblockAllOpen} busy={busy} onClose={() => setUnblockAllOpen(false)} onConfirm={unblockAll} />
    </Box>
  );
}
